import { supabase } from '../config/supabase';
import { hireCodeRatioSettingsService, HireCodeRatioConfig } from './hireCodeRatioSettingsService';

interface Owner {
  id: string;
  role: string;
  full_name: string;
  org_id: string;
  created_at: string;
}

interface OrganizationStats {
  totalEmployees: number;
  activeHires: number;
  completedOnboardings: number;
}

export const ownerService = {
  // Get the current owner's profile
  async getCurrentOwnerProfile(): Promise<Owner | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .eq('role', 'owner')
        .single();

      if (error) throw error;
      return profile;
    } catch (error) {
      console.error('Error fetching owner profile:', error);
      return null;
    }
  },

  // Get owner's organization ID
  async getOwnerOrgId(): Promise<string | null> {
    try {
      const profile = await this.getCurrentOwnerProfile();
      return profile?.org_id || null;
    } catch (error) {
      console.error('Error fetching owner org ID:', error);
      return null;
    }
  },

  // Get organization statistics
  async getOrganizationStats(): Promise<OrganizationStats | null> {
    try {
      const orgId = await this.getOwnerOrgId();
      if (!orgId) return null;

      // Get total team members (admins and owners with org_id)
      const { data: teamMembers, error: teamMembersError } = await supabase
        .from('profiles')
        .select('id')
        .eq('org_id', orgId)
        .in('role', ['admin', 'owner']);

      if (teamMembersError) throw teamMembersError;

      // Get active hires
      const { data: activeHires, error: hiresError } = await supabase
        .from('new_hire')
        .select('id')
        .eq('org_id', orgId);

      if (hiresError) throw hiresError;

      // Get completed onboardings (checkListNum = '14' means completed)
      const { data: completedOnboardings, error: completedError } = await supabase
        .from('new_hire')
        .select('id')
        .eq('org_id', orgId)
        .eq('checkListNum', '14');

      if (completedError) throw completedError;

      return {
        totalEmployees: teamMembers?.length || 0,
        activeHires: activeHires?.length || 0,
        completedOnboardings: completedOnboardings?.length || 0,
      };
    } catch (error) {
      console.error('Error fetching organization stats:', error);
      return null;
    }
  },

  // Get all team members (admins and owners) in the organization
  async getOrganizationTeamMembers(): Promise<any[]> {
    try {
      const orgId = await this.getOwnerOrgId();
      console.log('Owner org ID:', orgId);
      if (!orgId) return [];

      // Fetch both admins and owners from the same organization
      const { data: teamMembers, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('org_id', orgId)
        .in('role', ['admin', 'owner'])
        .order('created_at', { ascending: false });

      console.log('Organization team members (admins + owners):', teamMembers);
      if (error) throw error;
      return teamMembers || [];
    } catch (error) {
      console.error('Error fetching organization team members:', error);
      return [];
    }
  },

  // Get all employees in the organization (keeping for backward compatibility)
  async getOrganizationEmployees(): Promise<any[]> {
    return this.getOrganizationTeamMembers();
  },

  // Check if user has owner role
  async isOwner(): Promise<boolean> {
    try {
      const profile = await this.getCurrentOwnerProfile();
      return profile?.role === 'owner';
    } catch (error) {
      console.error('Error checking owner role:', error);
      return false;
    }
  },


  // Get current user's hire per code ratio (works for both admins and owners, all time - includes archived)
  async getCurrentUserHireRatio(): Promise<{
    enrolled: number;
    waiting: number;
    ratio: number | null;
  }> {
    try {
      console.log('[getCurrentUserHireRatio] Starting...');
      
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.log('[getCurrentUserHireRatio] Auth error or no user:', authError);
        return { enrolled: 0, waiting: 0, ratio: null };
      }
      console.log('[getCurrentUserHireRatio] User:', { id: user.id, email: user.email });

      // Get user profile to get org_id and email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, team_member_email, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        console.log('[getCurrentUserHireRatio] Profile error:', profileError, 'Profile:', profile);
        return { enrolled: 0, waiting: 0, ratio: null };
      }
      console.log('[getCurrentUserHireRatio] Profile:', profile);

      const orgId = profile.org_id;
      if (!orgId) {
        console.log('[getCurrentUserHireRatio] No org_id found');
        return { enrolled: 0, waiting: 0, ratio: null };
      }

      // Use team_member_email from profile, fallback to email from auth user
      // For admins, use user.email (same as getNewHires uses) to ensure consistency
      const teamMemberEmail = profile.team_member_email || user.email || '';
      if (!teamMemberEmail) {
        console.error('[getCurrentUserHireRatio] No email found for user:', { profile, userEmail: user.email });
        return { enrolled: 0, waiting: 0, ratio: null };
      }

      console.log('[getCurrentUserHireRatio] Calculating hire ratio for:', { teamMemberEmail, orgId, profileRole: profile.role });

      const ratioConfig: HireCodeRatioConfig = await hireCodeRatioSettingsService.getSettings(Number(orgId));
      const emailPattern = `%${teamMemberEmail}%`;

      const getCount = async (
        table: 'new_hire' | 'new_hire_archive',
        { progressValues }: { progressValues: number[] },
      ): Promise<number> => {
        if (progressValues.length === 0) return 0;

        let query = supabase
          .from(table)
          .select('*', { count: 'exact', head: true })
          .eq('org_id', orgId)
          .ilike('recruiter_emails', emailPattern)
          .in('progress', progressValues);

        const { count, error } = await query;
        if (error) throw error;
        return count ?? 0;
      };

      const [
        enrolledActive,
        enrolledArchived,
        waitingActive,
        waitingArchived,
      ] = await Promise.all([
        getCount('new_hire', { progressValues: ratioConfig.hire_stages }),
        getCount('new_hire_archive', { progressValues: ratioConfig.hire_stages }),
        getCount('new_hire', { progressValues: ratioConfig.coded_stages }),
        getCount('new_hire_archive', { progressValues: ratioConfig.coded_stages }),
      ]);

      const enrolled = (enrolledActive || 0) + (enrolledArchived || 0);
      const waiting = (waitingActive || 0) + (waitingArchived || 0);
      const ratio = enrolled === 0 ? null : waiting / enrolled;

      console.log('Hire ratio calculation:', {
        enrolledActive,
        enrolledArchived,
        waitingActive,
        waitingArchived,
        enrolled,
        waiting,
        ratio,
        ratioConfig,
        emailPattern,
        teamMemberEmail
      });

      return { enrolled, waiting, ratio };
    } catch (error) {
      console.error('Error fetching current user hire ratio:', error);
      return { enrolled: 0, waiting: 0, ratio: null };
    }
  }
}; 